import React from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { Box } from '@mui/material';
import { fadeUp } from './variants';
import { useMotionSafe } from './useMotionSafe';
import type { MotionBoxProps } from './types';

interface RevealProps extends MotionBoxProps {
  children: React.ReactNode;
  /** Which entrance to use. Defaults to a gentle rise-and-fade. */
  variants?: Variants;
  /** Seconds to wait before starting. */
  delay?: number;
  /** How far into the viewport the element must be before it fires. */
  margin?: string;
  /** Animate every time it scrolls into view, instead of just the first. */
  repeat?: boolean;
}

const MotionBox = motion.create(Box);

/**
 * Reveals its children when they scroll into view.
 *
 * Used for marketing sections — deliberately *not* used inside dashboards,
 * where content should be present the moment it renders.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  variants = fadeUp,
  delay = 0,
  margin = '-80px',
  repeat = false,
  ...boxProps
}) => {
  const { enabled, safeVariants } = useMotionSafe();

  if (!enabled) {
    return <Box {...boxProps}>{children}</Box>;
  }

  return (
    <MotionBox
      initial="hidden"
      whileInView="visible"
      viewport={{ once: !repeat, margin }}
      variants={safeVariants(variants)}
      transition={{ delay }}
      {...boxProps}
    >
      {children}
    </MotionBox>
  );
};

export default Reveal;
